import {
  Typography,
  Box,
  Grid,
  Button,
  useMediaQuery,
  Snackbar,
  Alert,
  TextField,
  Collapse,
  IconButton,
  CircularProgress,
  Chip,
} from '@mui/material';
import {useContext, useState, useEffect, useCallback} from 'react';
import mainLogo from '../../images/logo_black_letters_2.png';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import {BasicPlanCard, PremiumPlanCard} from '../../components/pricingOptions/planCard';
import {BasicPlanHeader, PremiumPlanHeader} from '../../components/pricingOptions/comparisonHeaders';
import {AllPlansDropdown} from '../../components/pricingOptions/dropdown';
import {mainLinearGradient} from '../../theme/gradients';
import MiscContext from '../../context/MiscContext';
import {GeneralInfoBase} from '../../components/formsBase/generalBase';
import {useCheckoutPaymentsApi} from '../../api/payment';
import {AnimationLoadingScreenWithProgressBar} from '../../components/loading/loadingScreen';
import {paymentsEnabled} from '../../constants';

const firstTextGridSpace = 5;

const comparingPlansHeaderStyles = {
  py: 2,
  pb: {
    xs: 0,
    md: 2,
  },
  gap: 4,
  justifyContent: 'space-between',
  position: 'sticky',
  top: 0,
  backgroundColor: '#F8FAFC !important',
  zIndex: 200,
  borderBottom: '1px solid #DBDCDC',
};

/**
 * PromoCodeSection component for handling promo code input
 */
const PromoCodeSection = ({
  promoCode,
  setPromoCode,
  promoApplied,
  setPromoApplied,
  promoError,
  setPromoError,
  isValidating,
  setIsValidating,
  onPromoApplied,
}) => {
  const [showPromoField, setShowPromoField] = useState(false);
  const paymentsApi = useCheckoutPaymentsApi();

  const handlePromoCodeChange = event => {
    setPromoCode(event.target.value);
    setPromoError('');
    setPromoApplied(false);
    // Reset discount when promo code changes
    onPromoApplied(null);
  };

  const validatePromoCode = async () => {
    if (!promoCode.trim()) return;

    setIsValidating(true);
    try {
      // Call the actual API to validate promo code and get pricing
      const monthlyResponse = await paymentsApi.postCheckout('monthly', promoCode);
      const yearlyResponse = await paymentsApi.postCheckout('yearly', promoCode);

      if (monthlyResponse.status === 200 && yearlyResponse.status === 200) {
        const discountInfo = {
          monthly: {
            originalPrice: monthlyResponse.data.original_price,
            finalPrice: monthlyResponse.data.final_price,
            discountAmount: monthlyResponse.data.discount_amount,
            currency: monthlyResponse.data.currency,
          },
          yearly: {
            originalPrice: yearlyResponse.data.original_price,
            finalPrice: yearlyResponse.data.final_price,
            discountAmount: yearlyResponse.data.discount_amount,
            currency: yearlyResponse.data.currency,
          },
        };

        setPromoApplied(true);
        setPromoError('');
        onPromoApplied(discountInfo);
      } else {
        throw new Error('Failed to validate promo code');
      }
    } catch (error) {
      // Handle the specific API error response format
      if (error.response && error.response.data && error.response.data.detail) {
        setPromoError(error.response.data.detail);
      } else {
        setPromoError('Error validating promo code. Please try again.');
      }
      setPromoApplied(false);
      onPromoApplied(null);
    } finally {
      setIsValidating(false);
    }
  };

  const handleKeyPress = event => {
    if (event.key === 'Enter') {
      validatePromoCode();
    }
  };

  return (
    <Box sx={{mb: 4, mx: 'auto', maxWidth: '600px'}}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          p: 2,
          backgroundColor: '#F0F9FF',
          borderRadius: '12px',
          mb: 1,
          border: '1px solid #E0F2FE',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: '#E0F2FE',
          },
        }}
        onClick={() => setShowPromoField(!showPromoField)}
      >
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
          <LocalOfferIcon sx={{color: '#0891B2'}} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: '#0F172A',
            }}
          >
            Have a promo code?
          </Typography>
        </Box>
        <IconButton size="small" sx={{color: '#0891B2'}}>
          {showPromoField ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={showPromoField}>
        <Box
          sx={{
            p: 3,
            backgroundColor: '#FAFBFC',
            borderRadius: '12px',
            border: '1px solid #E2E8F0',
            mb: 2,
          }}
        >
          <Box sx={{display: 'flex', gap: 2, mb: 2}}>
            <TextField
              fullWidth
              size="medium"
              placeholder="Enter promo code"
              value={promoCode}
              onChange={handlePromoCodeChange}
              onKeyPress={handleKeyPress}
              error={!!promoError}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: '#FFFFFF',
                },
              }}
            />
            <Button
              variant="contained"
              onClick={validatePromoCode}
              disabled={!promoCode.trim() || isValidating}
              sx={{
                minWidth: '120px',
                px: 3,
                borderRadius: '8px',
                background: 'linear-gradient(91.76deg, #31A6EE 0.45%, #35C6C9 53.85%, #A6E177 100%)',
                '&:hover': {
                  background: 'linear-gradient(91.76deg, #2563EB 0.45%, #2DD4BF 53.85%, #84CC16 100%)',
                },
                '&:disabled': {
                  background: '#E2E8F0',
                },
                color: 'white',
                fontWeight: 600,
              }}
            >
              {isValidating ? <CircularProgress size={20} sx={{color: 'white'}} /> : 'Apply'}
            </Button>
          </Box>

          {promoError && (
            <Alert severity="error" sx={{mb: 1, borderRadius: '8px'}}>
              {promoError}
            </Alert>
          )}

          {promoApplied && (
            <Alert severity="success" sx={{mb: 1, borderRadius: '8px'}}>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <Typography variant="body2">Promo code applied successfully! Prices updated below.</Typography>
                <Chip label={promoCode} size="small" color="success" variant="outlined" />
              </Box>
            </Alert>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

/**
 * Helper function to calculate discounted price
 */
const calculatePrice = (originalPrice, discountInfo, period) => {
  if (!discountInfo || !discountInfo[period]) {
    return originalPrice;
  }
  return discountInfo[period].finalPrice;
};

/**
 * Helper function to get discount amount
 */
const getDiscountAmount = (discountInfo, period) => {
  if (!discountInfo || !discountInfo[period]) {
    return 0;
  }
  return discountInfo[period].discountAmount;
};

/**
 * <subtitle - key>:[
 *  {
 *   description: <str>,
 *   tooltip <str>,
 *   values: {
 *     <plan's name - key> : value
 *    ...
 *    }
 *  }
 *  ...
 * ]
 **/
export let allPlans = {
  basic: {
    price: 0,
    link: 'basic link',
    PlanCardComponent: BasicPlanCard,
    HeaderCardComponent: BasicPlanHeader,
  },
  premium: {
    price: isAnnual => (isAnnual ? 32 : 44),
    link: isAnnual => (isAnnual ? 'premium link 1' : 'premium link 2'),
    PlanCardComponent: PremiumPlanCard,
    HeaderCardComponent: PremiumPlanHeader,
  },
};

const staticData = {
  'Vital metrics': [
    {
      description: 'Systolic blood pressure',
      tooltip: 'The peak pressure in your brachial arteries during the contraction of your heart muscle.',
      planValues: {
        basic: 'check',
        premium: 'check',
      },
    },
    {
      description: 'Diastolic blood pressure',
      tooltip: 'The amount of pressure in your brachial arteries when your heart muscle is relaxed.',
      planValues: {
        basic: 'check',
        premium: 'check',
      },
    },
    {
      description: 'Heart rate variability',
      tooltip: 'The variability in the timing between one of your heartbeats and the next.',
      planValues: {
        basic: 'check',
        premium: 'check',
      },
    },
    {
      description: 'Breathing rate',
      tooltip: 'The number of times you inhale then exhale per minute.',
      planValues: {
        basic: 'check',
        premium: 'check',
      },
    },
    {
      description: 'Cardiac workload',
      tooltip: 'A measure of the stress put on your heart muscle.',
      planValues: {
        basic: 'check',
        premium: 'check',
      },
    },
    {
      description: 'Pulse rate',
      tooltip: 'The number of times your heart beats per minute.',
      planValues: {
        basic: 'check',
        premium: 'check',
      },
    },
    {
      description: 'Irregular heart count',
      tooltip: 'The number of times your heart beats outside its normal rhythm.',
      planValues: {
        basic: 'xmark',
        premium: 'check',
      },
    },
    {
      description: 'Vascular capacity',
      tooltip: 'A measure of the elasticity of your blood vessels.',
      planValues: {
        basic: 'check',
        premium: 'check',
      },
    },
  ],
  'Physical metrics': [
    {
      description: 'Body mass index',
      tooltip: 'A measure of your tissue mass (muscle,fat and bone) adjusted for height.',
      planValues: {
        basic: 'xmark',
        premium: 'check',
      },
    },
    {
      description: 'Facial skin age',
      tooltip: 'An estimate of the age based on the condition of the surface of your face.',
      planValues: {
        basic: 'xmark',
        premium: 'check',
      },
    },
    {
      description: 'Mental stress index',
      tooltip: 'A prediction of your stress level based on the cortisol in your blood.',
      planValues: {
        basic: 'xmark',
        premium: 'check',
      },
    },
  ],
  'Diabetes risk metrics': [
    {
      description: 'Risk of HbA1c > 5.7%',
      tooltip: 'An estimate of the risk for your HbA1c level being over 5.7%, indicating a risk of pre-diabetes.',
      planValues: {
        basic: 'xmark',
        premium: 'check',
      },
    },
    {
      description: 'Risk of fasting blood glucose > 5.5 mmol/L',
      tooltip:
        'An estimate of the risk of your blood glucose being above 5.5 mmol/L after an 8-10 hour fast, indicating pre-diabetes.',
      planValues: {
        basic: 'xmark',
        premium: 'check',
      },
    },
    {
      description: 'Type II diabetes risk',
      tooltip: 'An estimate of the risk of being diagnosed with type 2 diabetes by your doctor.',
      planValues: {
        basic: 'xmark',
        premium: 'check',
      },
    },
  ],
  'General disease risk metrics': [
    {
      description: 'Heart attack risk',
      tooltip: 'An estimate of your likelihood of experiencing your first heart attack within the next 10 years.',
      planValues: {
        basic: 'xmark',
        premium: 'check',
      },
    },
    {
      description: 'Hypertriglyceridemia disease',
      tooltip:
        'An estimate of your risk of having high triglyceride levels, indicating risk for cardiovascular disease.',
      planValues: {
        basic: 'xmark',
        premium: 'check',
      },
    },
    {
      description: 'Cardiovascular disease',
      tooltip:
        'An estimate of your likelihood of experiencing your first heart attack or stroke within the next 10 years.',
      planValues: {
        basic: 'xmark',
        premium: 'check',
      },
    },
    {
      description: 'Hypercholesterolemia risk',
      tooltip:
        'An estimate of your risk of having high bad cholesterol levels, indicating risk for cardiovascular disease.',
      planValues: {
        basic: 'xmark',
        premium: 'check',
      },
    },
    {
      description: 'Stroke risk',
      tooltip: 'An estimate of your likelihood of experiencing your first stroke within the next 10 years.',
      planValues: {
        basic: 'xmark',
        premium: 'check',
      },
    },
    {
      description: 'Hypertension risk',
      tooltip: 'An estimate of your risk of being diagnosed with hypertension(high blood pressure) by your doctor.',
      planValues: {
        basic: 'xmark',
        premium: 'check',
      },
    },
  ],
};

/**
 * React functional component representing the Pricing Options Base.
 * @component
 * @returns {JSX.Element} The rendered PricingOptionsBase component.
 */
export const PricingOptionsBase = () => {
  const [annualSelected, setAnnualSelected] = useState(true);
  // only applies for medium viewports
  const {userPlan: currentUserPlan} = useContext(MiscContext);
  const [currentSelectedPlan, setCurrentSelectedPlan] = useState('basic');
  const [currentPrice, setCurrentPrice] = useState(allPlans.basic.price);
  const [currentPaymentLink, setCurrentPaymentLink] = useState(allPlans.basic.link);
  const [waitingCheckoutLink, setWaitingCheckoutLink] = useState(false);
  const [responseReturned, setResponseReturned] = useState(false);
  const [message, setMessage] = useState(undefined);
  const paymentsApi = useCheckoutPaymentsApi();

  // Promo code states
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [discountInfo, setDiscountInfo] = useState(null);

  // uses medium breakpoint defined by Material UI
  const pastMediumViewPort = useMediaQuery('(min-width:900px)');

  // Handle promo code application
  const handlePromoApplied = useCallback(newDiscountInfo => {
    setDiscountInfo(newDiscountInfo);
  }, []);

  // Calculate current price with discount
  const getCurrentPrice = useCallback(() => {
    if (currentSelectedPlan === 'basic') {
      return allPlans.basic.price;
    }
    if (currentSelectedPlan === 'premium') {
      const originalPrice = allPlans.premium.price(annualSelected);
      const period = annualSelected ? 'yearly' : 'monthly';
      return calculatePrice(originalPrice, discountInfo, period);
    }
    return 0;
  }, [currentSelectedPlan, annualSelected, discountInfo]);

  // Get discount amount for current selection
  const getCurrentDiscount = useCallback(() => {
    if (currentSelectedPlan === 'premium' && discountInfo) {
      const period = annualSelected ? 'yearly' : 'monthly';
      return getDiscountAmount(discountInfo, period);
    }
    return 0;
  }, [currentSelectedPlan, annualSelected, discountInfo]);

  // Get original price for comparison
  const getOriginalPrice = useCallback(() => {
    if (currentSelectedPlan === 'basic') {
      return allPlans.basic.price;
    }
    if (currentSelectedPlan === 'premium') {
      return allPlans.premium.price(annualSelected);
    }
    return 0;
  }, [currentSelectedPlan, annualSelected]);

  // Get yearly price with discount
  const getCurrentYearlyPrice = useCallback(() => {
    if (currentSelectedPlan === 'premium' && discountInfo && discountInfo.yearly) {
      return discountInfo.yearly.finalPrice * 12;
    }
    return currentPrice * 12;
  }, [currentSelectedPlan, discountInfo, currentPrice]);

  // Get original yearly price
  const getOriginalYearlyPrice = useCallback(() => {
    if (currentSelectedPlan === 'premium' && discountInfo && discountInfo.yearly) {
      return discountInfo.yearly.originalPrice * 12;
    }
    return getOriginalPrice() * 12;
  }, [currentSelectedPlan, discountInfo, getOriginalPrice]);

  useEffect(() => {
    setCurrentPrice(getCurrentPrice());
  }, [getCurrentPrice]);

  const handleCheckout = useCallback(async () => {
    if (!paymentsEnabled) return;

    setWaitingCheckoutLink(true);
    try {
      if (currentUserPlan === 'basic') {
        // Use the promo code if it's applied, otherwise use empty string
        const finalPromoCode = promoApplied ? promoCode : '';
        const checkoutLinkReq = await paymentsApi.postCheckout(annualSelected ? 'yearly' : 'monthly', finalPromoCode);
        if (checkoutLinkReq.status !== 200) {
          setResponseReturned(true);
          setWaitingCheckoutLink(false);
          setMessage('An error occurred. Please try later.');
          return;
        }
        const link = checkoutLinkReq.data.redirect_url;
        window.location.href = link;
      }

      await paymentsApi.getPortal().then(res => {
        window.location.href = res.data.redirect_url;
      });
      return;
    } catch (error) {
      console.error('Error during checkout:', error);
      setResponseReturned(true);
      setWaitingCheckoutLink(false);

      // Handle specific API error responses
      if (error.response && error.response.data && error.response.data.detail) {
        setMessage(error.response.data.detail);
      } else {
        setMessage('An error occurred. Please try later.');
      }
    }
  }, [currentUserPlan, currentSelectedPlan, annualSelected, promoApplied, promoCode]);

  return waitingCheckoutLink ? (
    <AnimationLoadingScreenWithProgressBar title="Loading..." secondsToLoad={30} isAlmostComplete={responseReturned} />
  ) : (
    <GeneralInfoBase>
      <Grid
        container
        sx={{
          minHeight: '100vh',
          position: 'relative',
          zIndex: 60,
          backgroundColor: '#F8FAFC',
          pt: 3,
          flexDirection: 'column',
          //most of the design relies in the padding
          px: {
            xs: 2,
            sm: 5,
            lg: 10,
            xl: 20,
          },
          pb: {
            xs: 0,
            md: 8,
          },
        }}
      >
        {/* pricing options section */}
        <Grid
          item
          xs={12}
          container
          className="planCardsSection"
          sx={{
            position: 'relative',
            gap: 6,
            pb: 6,
            width: '100%',
            zIndex: 100,
            backgroundColor: '#F8FAFC',
          }}
        >
          <Grid item xs={12} sx={{display: 'flex', justifyContent: 'center'}}>
            <img src={mainLogo} alt="AIMD-logo" width={'170px'} />
          </Grid>
          <Grid item xs={12}>
            <Typography
              variant="h3"
              sx={{
                fontSize: '45px',
                fontWeight: 700,
                textAlign: 'center',
              }}
            >
              Pricing Overview
            </Typography>
          </Grid>

          {/* Promo Code Section */}
          <Grid item xs={12}>
            <PromoCodeSection
              promoCode={promoCode}
              setPromoCode={setPromoCode}
              promoApplied={promoApplied}
              setPromoApplied={setPromoApplied}
              promoError={promoError}
              setPromoError={setPromoError}
              isValidating={isValidating}
              setIsValidating={setIsValidating}
              onPromoApplied={handlePromoApplied}
            />
          </Grid>

          <Grid
            item
            xs={12}
            container
            direction="row"
            sx={{
              justifyContent: 'center',
              position: 'relative',
              zIndex: 100,
            }}
            className="cardItems-container"
            gap={{
              xs: 5,
              lg: 6,
            }}
          >
            {Object.keys(allPlans).map((plan, idx) => {
              const Card = allPlans[plan].PlanCardComponent;

              // Calculate prices for premium plan with discount
              let cardProps = {};
              if (plan === 'premium') {
                const originalMonthlyPrice = allPlans[plan].price(false);
                const originalYearlyPrice = allPlans[plan].price(true);

                const discountedMonthlyPrice = calculatePrice(originalMonthlyPrice, discountInfo, 'monthly');
                const discountedYearlyPrice = calculatePrice(originalYearlyPrice, discountInfo, 'yearly');

                cardProps = {
                  switchValue: annualSelected || false,
                  price: annualSelected ? discountedYearlyPrice : discountedMonthlyPrice,
                  originalPrice: annualSelected ? originalYearlyPrice : originalMonthlyPrice,
                  yearlyPrice: discountedYearlyPrice, // Pass the discounted MONTHLY price for yearly billing
                  originalYearlyPrice: originalYearlyPrice, // Pass the original MONTHLY price for yearly billing
                  discountAmount: annualSelected
                    ? getDiscountAmount(discountInfo, 'yearly')
                    : getDiscountAmount(discountInfo, 'monthly'),
                  switchOnChange: (_, newVal) => setAnnualSelected(newVal),
                  hasDiscount: promoApplied && discountInfo,
                };
              }
              return (
                <Grid
                  key={idx}
                  item
                  sm={12}
                  sx={{
                    maxWidth: {
                      lg: '440px !important',
                    },
                    position: 'relative',
                  }}
                >
                  {/* Promo Applied Indicator */}
                  {promoApplied && plan === 'premium' && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -10,
                        right: -10,
                        zIndex: 10,
                        bottom: 2,
                      }}
                    >
                      <Chip
                        label={`Discount Applied`}
                        color="success"
                        sx={{
                          fontWeight: 600,
                          '& .MuiChip-label': {
                            px: 2,
                          },
                        }}
                      />
                    </Box>
                  )}
                  <Card onClick={handleCheckout} isCurrentUserPlan={plan === currentUserPlan} {...cardProps} />
                </Grid>
              );
            })}
          </Grid>
        </Grid>
        {/* Comparing plans section */}
        {pastMediumViewPort ? (
          <>
            <Grid item xs={12} container className="comparingPlansSectionHeader" sx={comparingPlansHeaderStyles}>
              <Grid item xs={12} md={firstTextGridSpace}>
                <Typography variant="h4">Plan Comparison</Typography>
              </Grid>
              {Object.keys(allPlans).map((plan, idx, arr) => {
                const Header = allPlans[plan].HeaderCardComponent;
                const gridSize = (12 - firstTextGridSpace) / arr.length - 1;

                // Calculate props for premium header with discount
                let headerProps = {};
                if (Header === PremiumPlanHeader) {
                  const originalMonthlyPrice = allPlans[plan].price(false);
                  const originalYearlyPrice = allPlans[plan].price(true);

                  const discountedMonthlyPrice = calculatePrice(originalMonthlyPrice, discountInfo, 'monthly');
                  const discountedYearlyPrice = calculatePrice(originalYearlyPrice, discountInfo, 'yearly');

                  headerProps = {
                    switchValue: annualSelected || false,
                    price: annualSelected ? discountedYearlyPrice : discountedMonthlyPrice,
                    originalPrice: annualSelected ? originalYearlyPrice : originalMonthlyPrice,
                    yearlyPrice: discountedYearlyPrice * 12, // Discounted yearly total
                    originalYearlyPrice: originalYearlyPrice * 12, // Original yearly total
                    discountAmount: annualSelected
                      ? getDiscountAmount(discountInfo, 'yearly')
                      : getDiscountAmount(discountInfo, 'monthly'),
                    switchOnChange: (_, newVal) => setAnnualSelected(newVal),
                    hasDiscount: promoApplied && discountInfo,
                  };
                }

                return (
                  <Grid item xs={gridSize} key={idx} sx={{position: 'relative'}}>
                    {/* Promo Applied Indicator for Header */}
                    {promoApplied && plan === 'premium' && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -5,
                          right: -5,
                          zIndex: 10,
                        }}
                      >
                        <Chip
                          label={`Discount Applied`}
                          color="success"
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.7rem',
                          }}
                        />
                      </Box>
                    )}
                    <Header onClick={handleCheckout} isCurrentUserPlan={plan === currentUserPlan} {...headerProps} />
                  </Grid>
                );
              })}
            </Grid>
            <Grid item xs={12} container className="comparingPlansAccordion">
              <Grid item xs={12}>
                {Object.keys(staticData).map((subtitle, idx) => {
                  return <AllPlansDropdown key={idx} title={subtitle} descriptionsData={staticData[subtitle]} />;
                })}
              </Grid>
            </Grid>
          </>
        ) : (
          <TabContext value={currentSelectedPlan}>
            <Grid item xs={12} container className="comparingPlansSectionHeader" sx={comparingPlansHeaderStyles}>
              <Grid item xs={12}>
                <Typography variant="h4">Plan Comparison</Typography>
              </Grid>
              <Grid item xs={12}>
                <TabList
                  onChange={(_, newVal) => {
                    setCurrentSelectedPlan(newVal);
                  }}
                  sx={{
                    '& .MuiButtonBase-root': {
                      fontSize: 18,
                    },
                    '& .MuiButtonBase-root.Mui-selected': {
                      fontWeight: 700,
                    },
                  }}
                >
                  {Object.keys(allPlans).map((plan, idx) => {
                    return (
                      <Tab
                        value={plan}
                        key={idx}
                        label={
                          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 2}}>
                            {plan.charAt(0).toUpperCase() + plan.substring(1)}
                            {promoApplied && plan === 'premium' && (
                              <Chip
                                label={`Discount Applied`}
                                color="success"
                                size="small"
                                sx={{
                                  height: 16,
                                  fontSize: '0.6rem',
                                  '& .MuiChip-label': {
                                    px: 1,
                                  },
                                }}
                              />
                            )}
                          </Box>
                        }
                      />
                    );
                  })}
                </TabList>
              </Grid>
            </Grid>
            <Grid item xs={12} container className="comparingPlansAccordion">
              {Object.keys(staticData).map((subtitle, idx) => {
                //just showing one plan
                const newValues = staticData[subtitle].map(data => {
                  return {
                    description: data.description,
                    tooltip: data.tooltip,
                    planValues: {
                      [currentSelectedPlan]: data.planValues[currentSelectedPlan],
                    },
                  };
                });
                return (
                  <Grid item xs={12} key={idx}>
                    <AllPlansDropdown title={subtitle} descriptionsData={newValues} />
                  </Grid>
                );
              })}
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                position: 'sticky',
                bottom: 0,
                backgroundColor: '#F8FAFC !important',
                zIndex: 50,
                px: {
                  xs: 0,
                  md: 4,
                },
                py: 2,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderTop: '1px solid #DBDCDC',
                gap: 2,
              }}
            >
              <Box>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                  {/* Show original price crossed out if there's a discount */}
                  {promoApplied && currentSelectedPlan === 'premium' && getCurrentDiscount() > 0 && (
                    <Typography
                      variant="h6"
                      sx={{
                        textDecoration: 'line-through',
                        color: '#6B7280',
                        fontWeight: 400,
                      }}
                    >
                      ${getOriginalPrice()}
                    </Typography>
                  )}
                  <Typography variant="h5" fontWeight={700} noWrap>
                    ${currentPrice} <span style={{fontWeight: 400, fontSize: 13}}>/mo</span>
                  </Typography>
                  {promoApplied && currentSelectedPlan === 'premium' && getCurrentDiscount() > 0 && (
                    <Chip
                      label={`-${getCurrentDiscount().toFixed(2)}`}
                      color="success"
                      size="small"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.7rem',
                      }}
                    />
                  )}
                </Box>
                {currentSelectedPlan === 'premium' && annualSelected && (
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    {/* Show original yearly price crossed out if there's a discount */}
                    {promoApplied && getCurrentDiscount() > 0 && (
                      <Typography
                        variant="h6"
                        sx={{
                          textDecoration: 'line-through',
                          color: '#6B7280',
                          fontWeight: 400,
                          fontSize: 16,
                        }}
                      >
                        ${getOriginalYearlyPrice().toFixed(0)}
                      </Typography>
                    )}
                    <Typography variant="h5" fontWeight={700} textAlign="center" sx={{fontSize: 16}}>
                      ${getCurrentYearlyPrice().toFixed(0)}
                      <span style={{fontWeight: 400, fontSize: 13}}> /yr</span>
                    </Typography>
                  </Box>
                )}
                {promoApplied && currentSelectedPlan === 'premium' && (
                  <Chip label="Discount applied" color="success" size="small" sx={{mt: 0.5, fontSize: '0.7rem'}} />
                )}
              </Box>
              <Button
                sx={{
                  background:
                    currentSelectedPlan === currentUserPlan
                      ? 'transparent'
                      : ` linear-gradient(white,white) padding-box,${mainLinearGradient} border-box`,
                  borderRadius: 5,
                  border: '3px solid transparent',
                  px: 2,
                }}
                onClick={handleCheckout}
              >
                <Typography
                  sx={{
                    background: mainLinearGradient,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontSize: 16,
                    fontWeight: 700,
                    textAlign: 'center',
                  }}
                >
                  {currentSelectedPlan === currentUserPlan ? 'You are on this plan' : 'Upgrade to premium'}
                </Typography>
              </Button>
            </Grid>
          </TabContext>
        )}
      </Grid>
      <Snackbar
        autoHideDuration={2000}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        open={message !== undefined}
        onClose={() => {
          setMessage(undefined);
        }}
      >
        <Alert
          severity="error"
          sx={{
            mt: 0.5,
            mb: 6,
            alignSelf: 'center',
            width: '90%', //even though is not 100% it looks like it is
            textAlign: 'center',
          }}
        >
          {message}
        </Alert>
      </Snackbar>
    </GeneralInfoBase>
  );
};
